import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// import InfoPopover from '../InfoPopover';
import { IconButton, Popover, Text } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import {
  StatusBlockContainer,
  StatusBlockContent,
  StatusDot,
  StatusTitle,
} from './StickyNavbar.styles';
import { device } from '../../styles/devices';

export interface StatusBlockProps {
  isAvailableNow: boolean;
  cartypeText: string;
  vehicleOrderStatus?: string | null;
  saleOrderTypeText?: string | null;
  market: string;
}

const ButtonStyled = styled(IconButton)`
  margin: 0;
`;
const Content = styled.div`
  @media ${device.tablet} {
    width: 290px;
  }
`;

const StatusBlock: React.FC<StatusBlockProps> = ({
  isAvailableNow,
  cartypeText,
  vehicleOrderStatus,
  saleOrderTypeText,
  market,
}) => {
  const { t } = useTranslation();
  const parentElementRef = useRef<HTMLDivElement>(undefined as unknown as HTMLDivElement);

  const [isOpen, setIsOpen] = useState(false);

  const marketType = market.includes('ca') ? 'CAN' : 'USA';

  const isReservedVehicle = saleOrderTypeText?.includes('SOLD');

  const statusForDot = useMemo(() => {
    if (isReservedVehicle) return 'REVERVED';
    if (isAvailableNow) return 'AVAILABLE';
    return 'PIPELINE';
  }, [isAvailableNow, isReservedVehicle]);

  const vehicleStatus = useMemo(() => {
    if (isReservedVehicle) return t('vehiclePricingBar.dealerListPrice.orderStatus.reserved');
    if (isAvailableNow)
      return t(`vehiclePricingBar.dealerListPrice.availableNow${marketType}.label`);
    if (!isAvailableNow && cartypeText === 'N' && vehicleOrderStatus === 'Intransit From Factory')
      return t('vehiclePricingBar.dealerListPrice.orderStatus.factory');
    if (!isAvailableNow && cartypeText === 'N' && vehicleOrderStatus === 'Port Stock')
      return t('vehiclePricingBar.dealerListPrice.orderStatus.port');
    if (!isAvailableNow && cartypeText === 'N' && vehicleOrderStatus === 'Intransit Inland')
      return t('vehiclePricingBar.dealerListPrice.orderStatus.inland');
    return t('vehiclePricingBar.dealerListPrice.orderStatus.contactDealer');
  }, [isReservedVehicle, t, isAvailableNow, marketType, cartypeText, vehicleOrderStatus]);

  const statusDescription = useMemo(() => {
    if (isReservedVehicle)
      return t('vehiclePricingBar.dealerListPrice.reservedStatus.popoverDescription');
    if (isAvailableNow && marketType === 'CAN')
      return t('vehiclePricingBar.dealerListPrice.availableNowCAN.popoverDescription');
    if (isAvailableNow && marketType !== 'CAN')
      return t('vehiclePricingBar.dealerListPrice.availableNowUSA.popoverDescription');
    if (!isAvailableNow && marketType === 'CAN')
      return t('vehiclePricingBar.dealerListPrice.availableSoon.popoverDescriptionCAN');
    if (!isAvailableNow && marketType !== 'CAN')
      return t('vehiclePricingBar.dealerListPrice.availableSoon.popoverDescriptionUSA');
    return '';
  }, [isAvailableNow, isReservedVehicle, marketType, t]);

  return (
    <>
      {cartypeText === 'N' && (
        <div ref={parentElementRef}>
          <StatusBlockContainer>
            <StatusBlockContent>
              <StatusDot statusForDot={statusForDot} />
              <StatusTitle data-testid="availability-status">{vehicleStatus}</StatusTitle>
              <Popover
                aria-label="status_popover"
                id="status_popover"
                triggerElement={
                  <ButtonStyled
                    variant="tertiary"
                    icon="info-circle"
                    size="sm"
                    onClick={() => setIsOpen(!isOpen)}
                  />
                }
                open={isOpen}
                content={
                  <Content>
                    <Text>{statusDescription}</Text>
                  </Content>
                }
                onClose={() => setIsOpen(false)}
                withTip
              />
            </StatusBlockContent>
          </StatusBlockContainer>
        </div>
      )}
    </>
  );
};

export default StatusBlock;
